import Swal from "sweetalert2";
import CurrencyFormatter from "currency-formatter";
import {
  updateFinancialsReportDateOrder,
  generateWorkingCapital,
  generateCashFlow,
  generateReconciliationOfValuationMethodsReport,
  setReconciliationOfValuationMethodsReport,
  generateOwnerFinancingStockSaleReport,
  generateAssetSaleProceedsWorksheet,
} from "../financials/actions";
import { generateBizminerRatios, generateDupontRatios, generateAltmanZScore } from "../financialRatios/actions";
import { generateHDEAnalysis, generateSDEAnalysis } from "../qualityOfEarnings/actions";
import { generateGrowthForecastStageDCF } from "../growthForecastStageDCF/actions";
import { generateAssumptions } from "../waterfall/actions";
import { setReportIsLoading } from "../appState/actions";
import { store } from "../../index";
import { SET_DASHBOARD_REPORT_DATA } from "./mutations";
import { ASCENDANT_ORDER, DESCENDANT_ORDER, YEARLY } from "../../../config";
import FinancingHelper from "../../../helpers/FinancingHelper";
import FinancialHandler from "../../../handlers/FinancialHandler";
import Converter from "../../../utils/Converter";

export const generateDashboardReportData = (
  charts = {
    forecastedRevenueChartElement: null,
    forecastedDOEChartElement: null,
  },
  dealType = "STOCK",
  reportType = "GENERAL",
  reportFormat = "PDF"
) => async (dispatch) => {
  const companyManuality = store.getState().appState.companyData.id === "manualCompany";

  //Required Initialization
  if(!companyManuality) {
    if(!store.getState().financials?.currentCompanyMonthlyBalanceSheet?.assets[0]?.items[0]?.category) {
      Swal.fire("Initialization Error!", "Please go to the \"Dashboard/Financials/Balance Sheet\" to select the categories.", "warning");
      return;
    }
    if(!store.getState().financials?.currentCompanyMonthlyProfitAndLoss?.incomes[0]?.category) {
      Swal.fire("Initialization Error!", "Please go to the \"Dashboard/Financials/Profit and Loss\" to select the categories.", "warning");
      return;
    }
  }
  if(!store.getState().financingCalculator.method) {
    Swal.fire("Initialization Error!", "Please go to the \"Dashboard/Transactions/Financing Calculator\" for initialization.", "warning");
    return;
  };
  
  //Calculation using dispatch
  await dispatch(updateFinancialsReportDateOrder(DESCENDANT_ORDER));
  await dispatch(generateBizminerRatios());
  await dispatch(generateDupontRatios({ dateOrder: ASCENDANT_ORDER, prefix: YEARLY }));
  await dispatch(generateAltmanZScore({ dateOrder: ASCENDANT_ORDER, prefix: YEARLY }));
  await dispatch(generateHDEAnalysis());
  await dispatch(generateSDEAnalysis());
  await dispatch(generateGrowthForecastStageDCF());
  await dispatch(setReconciliationOfValuationMethodsReport());   //contains all generation
  setTimeout(() => {
    dispatch(generateReconciliationOfValuationMethodsReport())
  }, 1000);
  await dispatch(generateOwnerFinancingStockSaleReport());
  await dispatch(generateAssetSaleProceedsWorksheet());
  await dispatch(generateAssumptions(dealType));
  await dispatch(generateCashFlow());
  await dispatch(generateWorkingCapital());
  await dispatch(updateFinancialsReportDateOrder(ASCENDANT_ORDER));

  const { taxes } = store.getState()

  const {
    currentCompanyMonthlyBalanceSheet: BS,
    currentCompanyMonthlyProfitAndLoss: PL,
    currentCompanyCashFlow: CF,
    currentCompanyWorkingCapital: WC,
    currentCompanyBalanceSheetReport: BSR,
    currentCompanyBalanceSheetReportSummary: BSRS,
    currentReconciliationOfValuationMethodsReport: REC,
    currentGoodwillReport: GWR,
    stockDealSplitReport: SDR,
    assetSaleDealSplitReport: ADR,
    ownerFinancingStockSaleReport: SPW,
    assetSaleProceedsWorksheet: APW,
  } = store.getState().financials

  const {
    currentCompanyBizminerRatios: BR,
    currentCompanyDupontRatios: DR,
    currentCompanyAltmanZScore: AZS,
  } = store.getState().financialRatios

  const { currentCompanyHDEAnalysis: HDE, currentCompanySDEAnalysis: SDE } = store.getState().qualityOfEarnings
  const { forecastedRevenue: FR, forecastedDOE: FDOE } = store.getState().valuation
  const { currentForecastRC } = store.getState().forecastRC
  const { currentForecastGWC } = store.getState().forecastGWC
  const { forecastMaintenanceAndGrowthCapex } = store.getState().forecastMGC
  const { forecastedDepreciation } = store.getState().forecastDepreciation
  const { forecastedFreeCashFlowAfterTax, forecastedFreeCashFlowBeforeTax } = store.getState().forecastFCF.forecastFreeCashFlow
  const { capitalizationRate } = store.getState().capitalizationRate
  const { afterTaxCapitalizationRate, preTaxCapitalizationRate } = capitalizationRate
  const { preTaxNetIncomeConversionFactor, cashToEarningsFactor } = store.getState().forecastCashToEarning.forecastCashToEarnings
  const { capitalizationOfEarnings } = store.getState().capitalizationOfEarnings
  const { growthForecastStageDCF } = store.getState().growthForecastStageDCF
  const { discountedCashFlowMethod } = store.getState().discountedCFM
  const { financingCalculator } = store.getState()
  const { stockPurchaserAcquisitionExpenses, assetPurchaserAcquisitionExpenses } = store.getState().acquisitionExpenses
  const { assumptions } = store.getState().waterfall
  const { generalInfo, businessDetails, valuationInfo, disclosure } = store.getState().generalData

  const {
    currentLBODashboard,
    dealDCFAnalysis,
    debtPanel,
    dealDCFValuation,
  } = store.getState().dealOffer

  let tokens = [];

  //tokens generation
  BS.yearsFormatted.map((year, index) => { tokens.push({ name: `mainyear${index + 1}`, value: year }) })
  if(!companyManuality) {
    BS.getYearlyTotalAssets("Current Assets").map((item, index) => { tokens.push({
      name: `CurrAssets${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalAssets("Fixed Assets").map((item, index) => { tokens.push({
      name: `FixedAssets${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalAssets().map((item, index) => { tokens.push({
      name: `TotAssets${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalLiabilities("Current Liabilities").map((item, index) => { tokens.push({
      name: `CurrLia${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalLiabilities("Long Term Liability").map((item, index) => { tokens.push({
      name: `LTLia${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalLiabilities().map((item, index) => { tokens.push({
      name: `TotLia${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    BS.getYearlyTotalEquities().map((item, index) => { tokens.push({
      name: `TotEquity${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalSales.map((item, index) => { tokens.push({
      name: `TotSales${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalCostOfSales.map((item, index) => { tokens.push({
      name: `TotCost${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalGrossProfits.map((item, index) => { tokens.push({
      name: `Profit${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalOperatingExpenses.map((item, index) => { tokens.push({
      name: `OprExp${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalOperatingIncome.map((item, index) => { tokens.push({
      name: `OprInc${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalOtherIncomes.map((item, index) => { tokens.push({
      name: `OtherInc${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalOtherExpenses.map((item, index) => { tokens.push({
      name: `OtherExp${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyEarningsBeforeIncomeTaxes.map((item, index) => { tokens.push({
      name: `EBIT${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalInterestIncome.map((item, index) => { tokens.push({
      name: `InterestInc${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalInterestExpense.map((item, index) => { tokens.push({
      name: `InterestExp${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalPreTaxNetProfits.map((item, index) => { tokens.push({
      name: `PreTaxProf${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalIncomeTax.map((item, index) => { tokens.push({
      name: `Tax${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalAfterTaxNetProfits.map((item, index) => { tokens.push({
      name: `AfterTaxProf${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalInterestExpense.map((item, index) => { tokens.push({
      name: `Interest${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalIncomeTax.map((item, index) => { tokens.push({
      name: `Taxes${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalDepreciationDepletionAndAmortization.map((item, index) => { tokens.push({
      name: `DDA${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyTotalDefinitionAdjustments.map((item, index) => { tokens.push({
      name: `DefAdjust${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    PL.yearlyEBITDAAsDefined.map((item, index) => { tokens.push({
      name: `EBITDA${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "USD" })
    }) })
    CF.yearlyTotalCashFromOperations.map((item, index) => { tokens.push({
      name: `CashOpr${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    CF.yearlyTotalCashFromInvesting.map((item, index) => { tokens.push({
      name: `CashInv${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    CF.yearlyTotalCashFromFinancing.map((item, index) => { tokens.push({
      name: `CashFin${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    CF.yearlyOpeningCashBalance.map((item, index) => { tokens.push({
      name: `OpenCash${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    CF.yearlyNetChangeInCash.map((item, index) => { tokens.push({
      name: `NetCash${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    CF.yearlyClosingCashBalance.map((item, index) => { tokens.push({
      name: `EndCash${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyDaysSalesOutstanding.map((item, index) => { tokens.push({
      name: `DaysSales${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    WC.yearlyDaysPayableOutstanding.map((item, index) => { tokens.push({
      name: `DaysPayable${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    WC.yearlyDaysInventoryOnHand.map((item, index) => { tokens.push({
      name: `DaysInv${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    WC.yearlyOtherCurrentAssetsPercentageOfSales.map((item, index) => { tokens.push({
      name: `AssetToSales${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" }) + "%"
    }) })
    WC.yearlyOtherLiabilitiesPercentageOfSales.map((item, index) => { tokens.push({
      name: `LiaToSales${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" }) + "%"
    }) })
    WC.yearlyCashWorkingCapitalRatio.map((item, index) => { tokens.push({
      name: `CashWCRatio${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" }) + "x"
    }) })
    WC.yearlyNonCashWorkingCapitalRatio.map((item, index) => { tokens.push({
      name: `NonCashWCRatio${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" }) + "x"
    }) })
    WC.yearlyInventoryTurns.map((item, index) => { tokens.push({
      name: `InvTurns${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" }) + "x"
    }) })
    WC.yearlyCashConversionCycleDays.map((item, index) => { tokens.push({
      name: `CycleDays${index + 1}`,
      value: item?.value === Infinity ? "Infinity" : CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    WC.yearlyCashNetWorkingCapital.map((item, index) => { tokens.push({
      name: `CashWC${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyCashNetWorkingCapitalTwelveMonthsTarget.map((item, index) => { tokens.push({
      name: `CashTarget${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyCashNetWorkingCapitalSixMonthsTarget.map((item, index) => { tokens.push({
      name: `Cash6Target${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyCashNetWorkingCapitalThreeMonthsTarget.map((item, index) => { tokens.push({
      name: `Cash3Target${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyNonCashNetWorkingCapital.map((item, index) => { tokens.push({
      name: `NonCashWC${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyNonCashNetWorkingCapitalTwelveMonthsTarget.map((item, index) => { tokens.push({
      name: `NonCashTarget${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyNonCashNetWorkingCapitalSixMonthsTarget.map((item, index) => { tokens.push({
      name: `NonCash6Target${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    WC.yearlyNonCashNetWorkingCapitalThreeMonthsTarget.map((item, index) => { tokens.push({
      name: `NonCash3Target${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.averageEmployees.map((item, index) => { tokens.push({
      name: `AverageEmp${index + 1}`,
      value: Number(item?.value) || 0
    }) })
    BR.yearlyWagesAndLaborCost.map((item, index) => { tokens.push({
      name: `LaborCost${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyAverageEmployeeWage.map((item, index) => { tokens.push({
      name: `AverageWage${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyOfficersCompensation.map((item, index) => { tokens.push({
      name: `OfficerComp${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyPreTaxNetProfit.map((item, index) => { tokens.push({
      name: `PTaxProfit${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyDiscretionaryOwnerEarnings.map((item, index) => { tokens.push({
      name: `DisOwnerEarnings${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyAccountsPayableBusinessRevenue.map((item, index) => { tokens.push({
      name: `AccPayToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyCurrentRatio.map((item, index) => { tokens.push({
      name: `CurrRatio${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyQuickRatio.map((item, index) => { tokens.push({
      name: `QuickRatio${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyDaysPayable.map((item, index) => { tokens.push({
      name: `DaysPay${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyCurrentLiabilitiesInventory.map((item, index) => { tokens.push({
      name: `CurrLiaToInv${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyNetWorkingCapitalBusinessRevenue.map((item, index) => { tokens.push({
      name: `WCToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyCostofSalesAccountsPayable.map((item, index) => { tokens.push({
      name: `CostToAccPay${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyCostofSalesInventory.map((item, index) => { tokens.push({
      name: `CostToInv${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyEBITDABusinessRevenue.map((item, index) => { tokens.push({
      name: `EBITDAToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyPreTaxReturnOnAssets.map((item, index) => { tokens.push({
      name: `PTaxToAssets${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyPreTaxReturnonNetWorth.map((item, index) => { tokens.push({
      name: `PTaxToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyPreTaxReturnonBusinessRevenue.map((item, index) => { tokens.push({
      name: `PTaxToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyAfterTaxReturnOnAssets.map((item, index) => { tokens.push({
      name: `ATaxToAssets${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyAfterTaxReturnOnNetWorth.map((item, index) => { tokens.push({
      name: `ATaxToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyAfterTaxReturnOnBusinessRevenue.map((item, index) => { tokens.push({
      name: `ATaxToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyPreTaxProfitEmployee.map((item, index) => { tokens.push({
      name: `PTaxProfToEmp${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyOwnerEarningsEmployee.map((item, index) => { tokens.push({
      name: `OwnerEToEmp${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyAssetsBusinessRevenue.map((item, index) => { tokens.push({
      name: `AssetsToRev${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyDaysInventory.map((item, index) => { tokens.push({
      name: `DaysInventory${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyDaysReceivables.map((item, index) => { tokens.push({
      name: `DaysRec${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyCurrentAssetTurnover.map((item, index) => { tokens.push({
      name: `CurrAssetTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyFixedAssetTurnover.map((item, index) => { tokens.push({
      name: `FixedAssetTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyGrossProfitMargin.map((item, index) => { tokens.push({
      name: `ProfitMargin${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlySalesPerEmployee.map((item, index) => { tokens.push({
      name: `SalesPerEmp${index + 1}`,
      value: FinancingHelper.bracketFormatter(item?.value)
    }) })
    BR.yearlyInventoryTurnover.map((item, index) => { tokens.push({
      name: `InvTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyReceivablesTurnover.map((item, index) => { tokens.push({
      name: `RecTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyTotalAssetTurnover.map((item, index) => { tokens.push({
      name: `AssetTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyWorkingCapitalTurnover.map((item, index) => { tokens.push({
      name: `WCTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyDaysWorkingCapital.map((item, index) => { tokens.push({
      name: `DaysWC${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyCashTurnover.map((item, index) => { tokens.push({
      name: `CashTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyEfficiencyIndex.map((item, index) => { tokens.push({
      name: `EffIndex${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyInterestCoverage.map((item, index) => { tokens.push({
      name: `InterestCov${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyCurrentLiabilitiesNetWorth.map((item, index) => { tokens.push({
      name: `CurrLiaToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyLongTermLiabilitiesNetWorth.map((item, index) => { tokens.push({
      name: `LTLiaToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyLoansNotesPayableNetWorth.map((item, index) => { tokens.push({
      name: `LNPayableToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyModifiedZScore.map((item, index) => { tokens.push({
      name: `ModZScore${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    BR.yearlyTotalLiabilitiesNetWorth.map((item, index) => { tokens.push({
      name: `LiaToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyFixedAssetsNetWorth.map((item, index) => { tokens.push({
      name: `FixedAssetToWorth${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    BR.yearlyPayablesTurnover.map((item, index) => { tokens.push({
      name: `PayablesTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    DR.yearlyOperatingProfitMargin.map((item, index) => { tokens.push({
      name: `OperProfitMargin${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    DR.yearlyAssetTurnover.map((item, index) => { tokens.push({
      name: `AssTO${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    DR.yearlyReturnonAsset.map((item, index) => { tokens.push({
      name: `ROA${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    DR.yearlyTaxBurden.map((item, index) => { tokens.push({
      name: `TaxBurden${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    DR.yearlyInterestBurden.map((item, index) => { tokens.push({
      name: `IntBurden${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    DR.yearlyFinancialLeverage.map((item, index) => { tokens.push({
      name: `FinLev${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    DR.yearlyReturnonEquity.map((item, index) => { tokens.push({
      name: `ROE${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    if(AZS.businessType === "NON_MANUFACTURING") tokens.push({ name: "BusinessType", value: "Non Manufacturing" });
    else if(AZS.businessType === "PRIVATE_MANUFACTURING") tokens.push({ name: "BusinessType", value: "Private Manufacturing" });
    else if(AZS.businessType === "PUBLIC_MANUFACTURING") tokens.push({ name: "BusinessType", value: "Public Manufacturing" });
    AZS.yearlyWCTotalAssets.map((item, index) => { tokens.push({
      name: `WCToAsset${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    AZS.yearlyRetainedEarningsTotalAssets.map((item, index) => { tokens.push({
      name: `REToAsset${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    AZS.yearlyEBITTotalAssets.map((item, index) => { tokens.push({
      name: `EBITToAsset${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    AZS.yearlyBookValueofEquityTotalLiabilities.map((item, index) => { tokens.push({
      name: `EBVToLia${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    AZS.yearlySalesTotalAssets.map((item, index) => { tokens.push({
      name: `SalesToAsset${index + 1}`,
      value: CurrencyFormatter.format(item?.value * 100, { code: "" }) + "%"
    }) })
    AZS.yearlyZScore.map((item, index) => { tokens.push({
      name: `ZScore${index + 1}`,
      value: CurrencyFormatter.format(item?.value, { code: "" })
    }) })
    AZS.yearlyZone.map((item, index) => { tokens.push({
      name: `Zone${index + 1}`,
      value: item?.value
    }) })
  }
  SDE.totalSalesRevenue.map((item, index) => { tokens.push({
    name: `QOERev${index + 1}`,
    value: FinancingHelper.bracketFormatter(item.value)
  }) })
  SDE.EBITDA.map((item, index) => { tokens.push({
    name: `QOEEBITDA${index + 1}`,
    value: FinancingHelper.bracketFormatter(item.value)
  }) })
  if(SDE.independence === "MANUAL") {
    SDE.manualNetIncome.map((item, index) => { tokens.push({
      name: `QOEIncome${index + 1}`,
      value: FinancingHelper.bracketFormatter(item.value)
    }) })
    SDE.manualInterest.map((item, index) => { tokens.push({
      name: `QOEInterest${index + 1}`,
      value: FinancingHelper.bracketFormatter(item.value)
    }) })
    SDE.manualTaxes.map((item, index) => { tokens.push({
      name: `QOETaxes${index + 1}`,
      value: FinancingHelper.bracketFormatter(item.value)
    }) })
    SDE.manualDepreciation.map((item, index) => { tokens.push({
      name: `QOEDepre${index + 1}`,
      value: FinancingHelper.bracketFormatter(item.value)
    }) })
    SDE.manualAmortization.map((item, index) => { tokens.push({
      name: `QOEAmort${index + 1}`,
      value: FinancingHelper.bracketFormatter(item.value)
    }) })
  }
  HDE.matrixOwnershipSalary.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SOS${row + 1}N`, value: item.name });
    tokens.push({ name: `SOS${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SOS${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  HDE.totalSocialSecurityTaxes.map((item, index) => {
    tokens.push({ name: `SSTax${index + 1}`, value: FinancingHelper.bracketFormatter(item.value) });
  })
  HDE.totalMedicareTaxes.map((item, index) => {
    tokens.push({ name: `SMTax${index + 1}`, value: FinancingHelper.bracketFormatter(item.value) });
  })
  tokens.push({ name: "SOSNote", value: HDE.matrixOwnershipSalary.note })
  HDE.totalOwnershipSalaryandPayrollTaxes.map((item, index) => { tokens.push({
    name: `SalaryAndTax${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.matrixOwnerRetirementContributions.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SORC${row + 1}N`, value: item.name });
    tokens.push({ name: `SORC${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SORC${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "SORCNote", value: HDE.matrixOwnerRetirementContributions.note })
  HDE.totalOwnerRetirementContributions.map((item, index) => { tokens.push({
    name: `RetireContri${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.matrixOwnerHealthInsuranceBenefits.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SOHI${row + 1}N`, value: item.name });
    tokens.push({ name: `SOHI${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SOHI${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "SOHINote", value: HDE.matrixOwnerHealthInsuranceBenefits.note })
  HDE.totalOwnerHealthInsuranceBenefits.map((item, index) => { tokens.push({
    name: `HealthBeni${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.matrixNonOperatingExpenses.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SNOE${row + 1}N`, value: item.name });
    tokens.push({ name: `SNOE${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SNOE${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "SNOENote", value: HDE.matrixNonOperatingExpenses.note })
  HDE.totalNonOperatingExpenses.map((item, index) => { tokens.push({
    name: `NonOperExp${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.matrixNonRecurringExpenses.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SNRE${row + 1}N`, value: item.name });
    tokens.push({ name: `SNRE${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SNRE${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "SNRENote", value: HDE.matrixNonRecurringExpenses.note })
  HDE.totalNonRecurringExpenses.map((item, index) => { tokens.push({
    name: `NonRecExp${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.matrixOverUnderPaymentAdjustments.matrix.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `SOUP${row + 1}N`, value: item.name });
    tokens.push({ name: `SOUP${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `SOUP${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "SOUPNote", value: HDE.matrixOverUnderPaymentAdjustments.note })
  HDE.totalOverUnderPaymentAdjustments.map((item, index) => { tokens.push({
    name: `OvUnPayAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  HDE.totalDiscretionaryOwnerEarnings.map((item, index) => { tokens.push({
    name: `HistoryDOE${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.matrixManagementAdj.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `REM${row + 1}N`, value: item.name });
    tokens.push({ name: `REM${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `REM${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "REMNote", value: SDE.noteManagement })
  SDE.totalManagementAdj.map((item, index) => { tokens.push({
    name: `TotalManageAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.EBITDAManagementAdj.map((item, index) => { tokens.push({
    name: `EBITDAManageAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.matrixDiligenceAdj.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `RED${row + 1}N`, value: item.name });
    tokens.push({ name: `RED${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `RED${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "REDNote", value: SDE.noteDiligence })
  SDE.totalDiligenceAdj.map((item, index) => { tokens.push({
    name: `TotalDiliAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.EBITDADiligenceAdj.map((item, index) => { tokens.push({
    name: `EBITDADiliAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.matrixProFormaAdj.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `REP${row + 1}N`, value: item.name });
    tokens.push({ name: `REP${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `REP${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  tokens.push({ name: "REPNote", value: SDE.noteProForma })
  SDE.totalProFormaAdj.map((item, index) => { tokens.push({
    name: `TotalPFAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.EBITDAProFormaAdj.map((item, index) => { tokens.push({
    name: `EBITDAPFAdj${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.matrixOwnerSalaryAdj.map((item, row) => {
    if(item.name == "") return;
    tokens.push({ name: `ROS${row + 1}N`, value: item.name });
    tokens.push({ name: `ROS${row + 1}C`, value: item.comment });
    item.series.map((price, col) => {
      tokens.push({ name: `ROS${row + 1}${col + 1}`, value: FinancingHelper.bracketFormatter(price.value) });
    })
  })
  SDE.totalSocialSecurityTaxes.map((item, index) => {
    tokens.push({ name: `RSTax${index + 1}`, value: FinancingHelper.bracketFormatter(item.value) });
  })
  SDE.totalMedicareTaxes.map((item, index) => {
    tokens.push({ name: `RMTax${index + 1}`, value: FinancingHelper.bracketFormatter(item.value) });
  })
  tokens.push({ name: "ROSNote", value: SDE.noteOwnerSalary })
  SDE.totalOwnerSalaryAdj.map((item, index) => { tokens.push({
    name: `OwnerSalary${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  SDE.EBITDAOwnerSalaryAdj.map((item, index) => { tokens.push({
    name: `SellerDOE${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  tokens.push({ name: "futureyear", value: FR.yearsTotal[FR.yearsTotal.length - 1] })
  FR.salesRevenue.map((item, index) => { tokens.push({
    name: `Revenue${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  tokens.push({ name: "RevEsti", value: FinancingHelper.bracketFormatter(FR.forecastedValue) })
  FR.yoyArray.map((item, index) => { tokens.push({
    name: `RevYoYChange${index + 1}`,
    value: item === null ? "" : item + "%"
  }) })
  tokens.push({ name: "GrowthRate", value: FR.growthRate * 100 + "%" })
  tokens.push({ name: "ForecastedRev", value: FinancingHelper.bracketFormatter(FR.forecastedRevenue) })
  tokens.push({ name: "ForecastRevNote", value: FR.analystNote })
  tokens.push({ name: "DOESource", value: `Discretionary Earnings ${FDOE.source}` })
  FDOE.DOE.map((item, index) => { tokens.push({
    name: `DOE${index + 1}`,
    value: FinancingHelper.bracketFormatter(item?.value)
  }) })
  FDOE.yoyArray.map((item, index) => { tokens.push({
    name: `DOEYoYChange${index + 1}`,
    value: item === null ? "" : item + "%"
  }) })
  tokens.push({ name: "DOEEsti", value: FinancingHelper.bracketFormatter(FDOE.forecastedValue) })
  tokens.push({ name: "ForecastedDOE", value: FinancingHelper.bracketFormatter(FDOE.forecastedDOE) })
  tokens.push({ name: "ForecastedDOENote", value: FDOE.analystNote })
  tokens.push({ name: "Salary", value: FinancingHelper.bracketFormatter(currentForecastRC.salary) })
  tokens.push({ name: "Benifits", value: currentForecastRC.benefitsPercentage.toFixed(2) + "%" })
  tokens.push({ name: "RCEsti", value: "$" + currentForecastRC.reasonableCompensationEstimateFormatted })
  tokens.push({ name: "OnePlusGrowthRate", value: Number((1 + (taxes?.growthRate / 100))?.toFixed(2)) })
  tokens.push({ name: "ForecastedRC", value: "$" + currentForecastRC.forecastedReasonableCompensationFormatted })
  tokens.push({ name: "ForecastedRCNote", value: currentForecastRC.analystNote })
  tokens.push({ name: "NonCashWCPercent", value: currentForecastGWC?.nonCashWorkingCapitalPercentageRevenue?.toFixed(2) + "%" })
  tokens.push({ name: "NonCashWC", value: currentForecastGWC.nonCashWorkingCapitalAmountFormatted })
  tokens.push({ name: "FutureNonCashWC", value: currentForecastGWC.futureNonCashWorkingCapitalAmountFormatted })
  tokens.push({ name: "RequiredInvest", value: currentForecastGWC.requiredAnnualInvestmentFormatted })
  tokens.push({ name: "ForecastedGWCNote", value: currentForecastGWC.analystNote })
  tokens.push({ name: "CapexPercent", value: forecastMaintenanceAndGrowthCapex?.capexPercentageRevenue?.toFixed(2) + "%" })
  tokens.push({ name: "CapexAmount", value: FinancingHelper.bracketFormatter(forecastMaintenanceAndGrowthCapex.capitalExpenditureAmount) })
  tokens.push({ name: "ForecastedCapexAmount", value: FinancingHelper.bracketFormatter(forecastMaintenanceAndGrowthCapex.forecastedAnnualCapex) })
  tokens.push({ name: "ForecastedMGCNote", value: forecastMaintenanceAndGrowthCapex.analystNote })
  tokens.push({ name: "DeprePercent", value: forecastedDepreciation?.depreciationPercentageRevenue?.toFixed(2) + "%" })
  tokens.push({ name: "DepreAmount", value: FinancingHelper.bracketFormatter(forecastedDepreciation.depreciationAmount) })
  tokens.push({ name: "ForecastedDepreAmount", value: FinancingHelper.bracketFormatter(forecastedDepreciation.forecastedValue) })
  tokens.push({ name: "ForecastedDepNote", value: forecastedDepreciation.analystNote })
  tokens.push({ name: "ForecastedEBITDA", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.ebitda) })
  tokens.push({ name: "ForecastedEBIT", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.ebit) })
  tokens.push({ name: "StateTaxRate", value: taxes?.stateTax?.toFixed(2) + "%" })
  tokens.push({ name: "StateTax", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.stateIncomeTaxes) })
  tokens.push({ name: "NormalizedIncome", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.normalizedIncomeAfterStateTaxes) })
  tokens.push({ name: "FederalTaxRate", value: taxes?.federalTax?.toFixed(2) + "%" })
  tokens.push({ name: "FederalTax", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.federalIncomeTaxes) })
  tokens.push({ name: "NOPAT", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.nopat) })
  tokens.push({ name: "ATaxFCF", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowAfterTax.afterTaxFreeCashFlow) })
  tokens.push({ name: "PTaxFCF", value: FinancingHelper.bracketFormatter(forecastedFreeCashFlowBeforeTax.preTaxFreeCashFlow) })
  tokens.push({ name: "RiskFreeRate", value: afterTaxCapitalizationRate?.riskFreeRate?.toFixed(2) + "%" })
  tokens.push({ name: "EquRiskPrem", value: afterTaxCapitalizationRate?.equityRiskPremium?.toFixed(2) + "%" })
  tokens.push({ name: "SizePrem", value: afterTaxCapitalizationRate?.sizePremium?.toFixed(2) + "%" })
  tokens.push({ name: "IndRiskPrem", value: afterTaxCapitalizationRate?.industryRisk?.toFixed(2) + "%" })
  tokens.push({ name: "CouRisk", value: afterTaxCapitalizationRate?.countryRisk?.toFixed(2) + "%" })
  tokens.push({ name: "ComSpeRisk", value: afterTaxCapitalizationRate?.riskPremiumCompanySpecificRisks?.toFixed(2) + "%" })
  tokens.push({ name: "DisRate", value: afterTaxCapitalizationRate?.afterTaxNetCashFlowDiscountRate?.toFixed(2) + "%" })
  tokens.push({ name: "AfterTaxCapRate", value: afterTaxCapitalizationRate?.afterTaxNetCashFlowCapitalizationRate?.toFixed(2) + "%" })
  tokens.push({ name: "ATaxToPTaxRatio", value: preTaxCapitalizationRate?.ratioAfterToPreTax?.toFixed(2) + "%" })
  tokens.push({ name: "PreTaxCapRate", value: preTaxCapitalizationRate?.preTaxNetCashFlowCapitalizationRate?.toFixed(2) + "%" })
  tokens.push({ name: "PreTaxDisRate", value: preTaxCapitalizationRate?.preTaxNetCashFlowDiscountRate?.toFixed(2) + "%" })
  tokens.push({ name: "NoteCapRate1", value: afterTaxCapitalizationRate?.riskFreeRateNote })
  tokens.push({ name: "NoteCapRate2", value: afterTaxCapitalizationRate?.equityRiskPremiumNote })
  tokens.push({ name: "NoteCapRate3", value: afterTaxCapitalizationRate?.sizePremiumNote })
  tokens.push({ name: "NoteCapRate4", value: afterTaxCapitalizationRate?.industryRiskNote })
  tokens.push({ name: "NoteCapRate5", value: afterTaxCapitalizationRate?.countryRiskNote })
  tokens.push({ name: "NoteCapRate6", value: afterTaxCapitalizationRate?.riskPremiumCompanySpecificRisksNote })
  tokens.push({ name: "CapRateNote", value: capitalizationRate.analystNote })
  tokens.push({ name: "PredictedFactor", value: preTaxNetIncomeConversionFactor?.predictedFactor?.toFixed(2) + "%" })
  tokens.push({ name: "PreTaxIncCapRate", value: cashToEarningsFactor?.preTaxNetIncomeCapitalizationRate?.toFixed(2) + "%" })
  tokens.push({ name: "CashToEarnFactor", value: cashToEarningsFactor?.cashToEarningsFactor?.toFixed(2) + "%" })
  tokens.push({ name: "PTaxIncMultiple", value: cashToEarningsFactor?.preTaxIncomeMultiple?.toFixed(2) + "x" })
  tokens.push({ name: "CapEarnings", value: FinancingHelper.bracketFormatter(capitalizationOfEarnings?.afterTaxCapitalizedEarningsOfSubjectCompany) })
  tokens.push({ name: "TwoStepGrowthRate", value: growthForecastStageDCF?.analystChosenTwoStepGrowthRate?.toFixed(2) + "%" })
  tokens.push({ name: "ReinvestTotal", value: FinancingHelper.bracketFormatter(growthForecastStageDCF?.reinvestmentTotal) })
  tokens.push({ name: "NOPATReinvestRate", value: growthForecastStageDCF?.reinvestmentRateOnNetOperatingProfitAfterTax?.toFixed(2) + "%" })
  tokens.push({ name: "ReturnOnEquity", value: growthForecastStageDCF?.returnOnEquity?.toFixed(2) + "%" })
  tokens.push({ name: "SustainableGrowthRate", value: growthForecastStageDCF?.sustainableExpectedGrowthRate?.toFixed(2) + "%" })
  tokens.push({ name: "SustainableNote", value: growthForecastStageDCF?.sustainableNote })
  tokens.push({ name: "PTaxCFReinvestRate", value: growthForecastStageDCF?.reinvestmentRateOnPreTaxCashFlow?.toFixed(2) + "%" })
  tokens.push({ name: "ReturnOnAssets", value: growthForecastStageDCF?.returnOnAssets?.toFixed(2) + "%" })
  tokens.push({ name: "InternalGrowthRate", value: growthForecastStageDCF?.internalExpectedGrowthRate?.toFixed(2) + "%" })
  tokens.push({ name: "InternalNote", value: growthForecastStageDCF?.internalNote })
  tokens.push({ name: "YearsNumber", value: discountedCashFlowMethod?.totalYears })
  if(discountedCashFlowMethod?.discountFactor === 0.5) tokens.push({ name: "DiscountMethod", value: "Mid-Year Discount Factor" });
  else if(discountedCashFlowMethod?.discountFactor === 1) tokens.push({ name: "DiscountMethod", value: "Annual Discount Factor" });
  if(discountedCashFlowMethod?.method === 1) tokens.push({ name: "DCFMethod", value: "One Stage Method" });
  else if(discountedCashFlowMethod?.method === 2) tokens.push({ name: "DCFMethod", value: "Two Stage Method" });
  else if(discountedCashFlowMethod?.method === 3) tokens.push({ name: "DCFMethod", value: "Three Stage Method" });
  discountedCashFlowMethod?.yearsArray?.map((item, index) => { tokens.push({
    name: `DCFY${index + 1}`,
    value: item
  }) })
  // discountedCashFlowMethod?.threeStageGrowthForecast?.map((item, index) => { tokens.push({
  //   name: `TSGFP${index + 1}`,
  //   value: item?.toFixed(2) + "%"
  // }) })
  discountedCashFlowMethod?.freeCashFlow?.map((item, index) => { tokens.push({
    name: `FCF${index + 1}`,
    value: FinancingHelper.bracketFormatter(item)
  }) })
  discountedCashFlowMethod?.discountFactorArray?.map((item, index) => { tokens.push({
    name: `DF${index + 1}`,
    value: item + "Years"
  }) })
  discountedCashFlowMethod?.dividePresent?.map((item, index) => { tokens.push({
    name: `DR${index + 1}`,
    value: item?.toFixed(2)
  }) })
  discountedCashFlowMethod?.presentValue?.map((item, index) => { tokens.push({
    name: `PV${index + 1}`,
    value: FinancingHelper.bracketFormatter(item)
  }) })
  tokens.push({ name: "DCFGrowthRate", value: discountedCashFlowMethod?.averageGrowthRate?.toFixed(2) + "%" })
  tokens.push({ name: "DCFFCF", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.finalYearFreeCashFlow) })
  tokens.push({ name: "OnePlusGrowth", value: discountedCashFlowMethod?.terminalYear?.longTermGrowthRateMultiplier?.toFixed(2) })
  tokens.push({ name: "DCFFFCF", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.forecastedFreeCashFlow) })
  tokens.push({ name: "DCFCR", value: discountedCashFlowMethod?.terminalYear?.divideCapitalizationRate?.toFixed(2) })
  tokens.push({ name: "DCFTYV", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.terminalYearValue) })
  tokens.push({ name: "DCFPVF", value: discountedCashFlowMethod?.terminalDividePresent?.toFixed(2) })
  tokens.push({ name: "DCFPV", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.terminalPresentValue) })
  tokens.push({ name: "DCFPVCF", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.presentValueSum) })
  tokens.push({ name: "DCFVSC", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.valueSubjectCompany) })
  tokens.push({ name: "Delta", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.delta) })
  tokens.push({ name: "StepNote", value: FinancingHelper.bracketFormatter(discountedCashFlowMethod?.analystNote[discountedCashFlowMethod?.method]) })
  tokens.push({ name: "CABal", value: BSR[0].totalBalance })
  tokens.push({ name: "CAAdj", value: BSR[0].totalAdjustments })
  tokens.push({ name: "CAAdjVal", value: BSR[0].totalAdjustedValue })
  tokens.push({ name: "CALiqVal", value: BSR[0].totalLiquidationValue })
  tokens.push({ name: "CALiqPer", value: BSR[0].totalLiquidation })
  tokens.push({ name: "CAExcess", value: BSR[0].totalExcessOrNonOperating })
  tokens.push({ name: "FABal", value: BSR[1].totalBalance })
  tokens.push({ name: "FAAdj", value: BSR[1].totalAdjustments })
  tokens.push({ name: "FAAdjVal", value: BSR[1].totalAdjustedValue })
  tokens.push({ name: "FALiqVal", value: BSR[1].totalLiquidationValue })
  tokens.push({ name: "FALiqPer", value: BSR[1].totalLiquidation })
  tokens.push({ name: "FAExcess", value: BSR[1].totalExcessOrNonOperating })
  tokens.push({ name: "TABal", value: BSRS.assets.totalBalanceFormatted })
  tokens.push({ name: "TAAdj", value: BSRS.assets.totalAdjustmentsFormatted })
  tokens.push({ name: "TAAdjVal", value: BSRS.assets.totalAdjustedValueFormatted })
  tokens.push({ name: "TALiqVal", value: BSRS.assets.totalLiquidationValueFormatted })
  tokens.push({ name: "TALiqPer", value: BSRS.assets.totalLiquidationFormatted })
  tokens.push({ name: "TAExcess", value: BSRS.assets.totalExcessOrNonOperatingFormatted })
  tokens.push({ name: "CLBal", value: BSR[2].totalBalance })
  tokens.push({ name: "CLAdj", value: BSR[2].totalAdjustments })
  tokens.push({ name: "CLAdjVal", value: BSR[2].totalAdjustedValue })
  tokens.push({ name: "CLLiqVal", value: BSR[2].totalLiquidationValue })
  tokens.push({ name: "CLLiqPer", value: BSR[2].totalLiquidation })
  tokens.push({ name: "CLExcess", value: BSR[2].totalExcessOrNonOperating })
  tokens.push({ name: "LLBal", value: BSR[3].totalBalance })
  tokens.push({ name: "LLAdj", value: BSR[3].totalAdjustments })
  tokens.push({ name: "LLAdjVal", value: BSR[3].totalAdjustedValue })
  tokens.push({ name: "LLLiqVal", value: BSR[3].totalLiquidationValue })
  tokens.push({ name: "LLLiqPer", value: BSR[3].totalLiquidation })
  tokens.push({ name: "LLExcess", value: BSR[3].totalExcessOrNonOperating })
  tokens.push({ name: "TLBal", value: BSRS.liabilities.totalBalanceFormatted })
  tokens.push({ name: "TLAdj", value: BSRS.liabilities.totalAdjustmentsFormatted })
  tokens.push({ name: "TLAdjVal", value: BSRS.liabilities.totalAdjustedValueFormatted })
  tokens.push({ name: "TLLiqVal", value: BSRS.liabilities.totalLiquidationValueFormatted })
  tokens.push({ name: "TLLiqPer", value: BSRS.liabilities.totalLiquidationFormatted })
  tokens.push({ name: "TLExcess", value: BSRS.liabilities.totalExcessOrNonOperatingFormatted })
  tokens.push({ name: "EBal", value: BSRS.equity.totalBalanceFormatted })
  tokens.push({ name: "EAdj", value: BSRS.equity.totalAdjustmentsFormatted })
  tokens.push({ name: "EAdjVal", value: BSRS.equity.totalAdjustedValueFormatted })
  tokens.push({ name: "ELiqVal", value: BSRS.equity.totalLiquidationValueFormatted })
  tokens.push({ name: "ELiqPer", value: BSRS.equity.totalLiquidationFormatted })
  tokens.push({ name: "EExcess", value: BSRS.equity.totalExcessOrNonOperatingFormatted })
  tokens.push({ name: "AdjustedBookValNote", value: BSRS.totalNote })
  tokens.push({ name: "OrdLiqVal", value: REC.reportGroups[0].groupItems[0].formattedValue })
  tokens.push({ name: "OrdLiqWei", value: REC.reportGroups[0].groupItems[0].floatWeight + "%" })
  tokens.push({ name: "OrdLiqSum", value: REC.reportGroups[0].groupItems[0].formattedSummary })
  tokens.push({ name: "BookVal", value: REC.reportGroups[0].groupItems[1].formattedValue })
  tokens.push({ name: "BookWei", value: REC.reportGroups[0].groupItems[1].floatWeight + "%" })
  tokens.push({ name: "BookSum", value: REC.reportGroups[0].groupItems[1].formattedSummary })
  tokens.push({ name: "AdjBookVal", value: REC.reportGroups[0].groupItems[2].formattedValue })
  tokens.push({ name: "AdjBookWei", value: REC.reportGroups[0].groupItems[2].floatWeight + "%" })
  tokens.push({ name: "AdjBookSum", value: REC.reportGroups[0].groupItems[2].formattedSummary })
  tokens.push({ name: "CapEarnVal", value: REC.reportGroups[1].groupItems[0].formattedValue })
  tokens.push({ name: "CapEarnWei", value: REC.reportGroups[1].groupItems[0].floatWeight + "%" })
  tokens.push({ name: "CapEarnSum", value: REC.reportGroups[1].groupItems[0].formattedSummary })
  tokens.push({ name: "DisCFMVal", value: REC.reportGroups[1].groupItems[1].formattedValue })
  tokens.push({ name: "DisCFMWei", value: REC.reportGroups[1].groupItems[1].floatWeight + "%" })
  tokens.push({ name: "DisCFMSum", value: REC.reportGroups[1].groupItems[1].formattedSummary })
  tokens.push({ name: "RegAnlVal", value: REC.reportGroups[2].groupItems[0].formattedValue })
  tokens.push({ name: "RegAnlWei", value: REC.reportGroups[2].groupItems[0].floatWeight + "%" })
  tokens.push({ name: "RegAnlSum", value: REC.reportGroups[2].groupItems[0].formattedSummary })
  tokens.push({ name: "GuideVal", value: REC.reportGroups[2].groupItems[1].formattedValue })
  tokens.push({ name: "GuideWei", value: REC.reportGroups[2].groupItems[1].floatWeight + "%" })
  tokens.push({ name: "GuideSum", value: REC.reportGroups[2].groupItems[1].formattedSummary })
  tokens.push({ name: "ExcessAssetsLiab", value: FinancingHelper.bracketFormatter(REC.excessAssetsOrLiabilities) })
  tokens.push({ name: "DetFinalWei", value: REC.formattedTotalWeight + "%" })
  tokens.push({ name: "DetFinalSum", value: REC.formattedTotalSummary })
  tokens.push({ name: "RecTotL", value: REC.formattedAdjustedLiabilities })
  tokens.push({ name: "RecEquVal", value: REC.formattedtotalEquitySummary })
  tokens.push({ name: "OwnIntWei", value: REC.minorityShareholderDiscount.ownershipInterestPercentage + "%" })
  tokens.push({ name: "OwnIntSum", value: REC.ownershipInterestAmountFormatted })
  tokens.push({ name: "DisConWei", value: REC.minorityShareholderDiscount.discountForControlPercentage + "%" })
  tokens.push({ name: "DisConSum", value: REC.discountForControlAmountFormatted })
  tokens.push({ name: "DisLackWei", value: REC.minorityShareholderDiscount.discountForLackOfMarketability + "%" })
  tokens.push({ name: "DisLackSum", value: REC.discountForLackOfMarketabilityAmountFormatted })
  tokens.push({ name: "DisDetFinVal", value: REC.minorityShareholderDiscountDeterminedFinalValueFormatted })
  tokens.push({ name: "DiscountNote", value: REC.minorityShareholderDiscount.middleNote })
  tokens.push({ name: "OfferPrice", value: FinancingHelper.bracketFormatter(financingCalculator?.offerPriceValue) })
  tokens.push({ name: "DownPercent", value: financingCalculator?.downPaymentPercentage + "%" })
  if(financingCalculator?.method === "None") tokens.push({ name: "AcqType", value: "None" });
  else if(financingCalculator?.method === "Stock") tokens.push({ name: "AcqType", value: "Stock Purchaser" });
  else if(financingCalculator?.method === "Asset") tokens.push({ name: "AcqType", value: "Asset Purchaser" });
  else if(financingCalculator?.method === "Manual") tokens.push({ name: "AcqType", value: "Manual Input" });
  tokens.push({ name: "AcqWC", value: FinancingHelper.bracketFormatter(financingCalculator?.acquisitionExpensesWorkingCapital) })
  tokens.push({ name: "FinCalculatorInterest", value: financingCalculator?.interestRate + "%" })
  tokens.push({ name: "MonthsNumber", value: financingCalculator?.monthlyPayments })
  tokens.push({ name: "FairStatus", value: financingCalculator?.offerPrice?.title })
  tokens.push({ name: "StockSalePrice", value: FinancingHelper.bracketFormatter(financingCalculator?.offerPrice?.rows[0]?.value) })
  tokens.push({ name: "DownPayment", value: FinancingHelper.bracketFormatter(financingCalculator?.cashNeeded?.rows[0]?.value) })
  tokens.push({ name: "AcqWC", value: FinancingHelper.bracketFormatter(financingCalculator?.cashNeeded?.rows[1]?.value) })
  tokens.push({ name: "CashInvested", value: FinancingHelper.bracketFormatter(financingCalculator?.cashNeeded?.rows[2]?.value) })
  tokens.push({ name: "SDCF", value: FinancingHelper.bracketFormatter(financingCalculator?.earningsGeneratedBusiness?.rows[0]?.value) })
  tokens.push({ name: "RC", value: FinancingHelper.bracketFormatter(financingCalculator?.reasonableCompensation?.rows[0]?.value) })
  tokens.push({ name: "GWC", value: FinancingHelper.bracketFormatter(financingCalculator?.reinvestmentAnalysis?.rows[0]?.value) })
  tokens.push({ name: "Capex", value: FinancingHelper.bracketFormatter(financingCalculator?.reinvestmentAnalysis?.rows[1]?.value) })
  tokens.push({ name: "CashforCO", value: FinancingHelper.bracketFormatter(financingCalculator?.reinvestmentAnalysis?.rows[2]?.value) })
  tokens.push({ name: "SDECF", value: FinancingHelper.bracketFormatter(financingCalculator?.investorReturn?.rows[0]?.value) })
  tokens.push({ name: "PurchaserRoR", value: financingCalculator?.investorReturn?.rows[1]?.value?.toFixed(2) + "%" })
  tokens.push({ name: "PurchaserReqR", value: FinancingHelper.bracketFormatter(financingCalculator?.investorReturn?.rows[2]?.value) })
  tokens.push({ name: "CashToService", value: FinancingHelper.bracketFormatter(financingCalculator?.debtService?.rows[0]?.value) })
  tokens.push({ name: "AmountFinanced", value: FinancingHelper.bracketFormatter(financingCalculator?.financingTerms?.rows[0]?.value) })
  tokens.push({ name: "MonthlyPaymentOnPN", value: FinancingHelper.bracketFormatter(financingCalculator?.financingTerms?.rows[3]?.value) })
  tokens.push({ name: "AnnualPaymentOnPN", value: FinancingHelper.bracketFormatter(financingCalculator?.financingTerms?.rows[4]?.value) })
  tokens.push({ name: "FinPrinAmount", value: FinancingHelper.bracketFormatter(financingCalculator?.financingResults?.rows[1]?.value) })
  tokens.push({ name: "TotalInterest", value: FinancingHelper.bracketFormatter(financingCalculator?.financingResults?.rows[2]?.value) })
  tokens.push({ name: "TotalDealConsi", value: FinancingHelper.bracketFormatter(financingCalculator?.financingResults?.rows[3]?.value) })
  tokens.push({ name: "FinCalSDE", value: FinancingHelper.bracketFormatter(financingCalculator?.cashFlowMetrics?.rows[0]?.value) })
  tokens.push({ name: "FinCalEBITDA", value: FinancingHelper.bracketFormatter(financingCalculator?.cashFlowMetrics?.rows[1]?.value) })
  tokens.push({ name: "FinCalFCF", value: FinancingHelper.bracketFormatter(financingCalculator?.cashFlowMetrics?.rows[2]?.value) })
  tokens.push({ name: "PriceToSDCF", value: financingCalculator?.sellingPriceMultiples?.rows[0]?.value?.toFixed(2) + "x" })
  tokens.push({ name: "PriceToEBITDA", value: financingCalculator?.sellingPriceMultiples?.rows[1]?.value?.toFixed(2) + "x" })
  tokens.push({ name: "PriceToFCF", value: financingCalculator?.sellingPriceMultiples?.rows[2]?.value?.toFixed(2) + "x" })
  tokens.push({ name: "EBITDAToRepay", value: financingCalculator?.debtServiceCoverageRatios?.rows[0]?.value?.toFixed(2) + "x" })
  tokens.push({ name: "AdjEBITDAToRepay", value: financingCalculator?.debtServiceCoverageRatios?.rows[1]?.value?.toFixed(2) + "x" })
  financingCalculator?.priceMatrix?.price?.map((obj, index) => {
    tokens.push({ name: `Price${index}`, value: FinancingHelper.bracketFormatter(obj.value) })
  })
  financingCalculator?.priceMatrix?.deal?.map((obj, index) => {
    tokens.push({ name: `Consi${index}`, value: FinancingHelper.bracketFormatter(obj.value) })
  })
  tokens.push({ name: "Cash", value: FinancingHelper.bracketFormatter(
    BSR[0]?.categories?.filter((obj) => obj.categoryValue === "CASH_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "Securities", value: FinancingHelper.bracketFormatter(
    BSR[0]?.categories?.filter((obj) => obj.categoryValue === "MARKETABLE_SECURITIES_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "AR", value: FinancingHelper.bracketFormatter(
    BSR[0]?.categories?.filter((obj) => obj.categoryValue === "ACCOUNTS_RECEIVABLE_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "Inventory", value: FinancingHelper.bracketFormatter(
    BSR[0]?.categories?.filter((obj) => obj.categoryValue === "INVENTORY_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "OtherCA", value: FinancingHelper.bracketFormatter(
    BSR[0]?.categories?.filter((obj) => obj.categoryValue === "OTHER_CURRENT_ASSET_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "TotalCA", value: FinancingHelper.bracketFormatter(BSR[0]?.totalAdjustedValue) })
  tokens.push({ name: "RealEstate", value: FinancingHelper.bracketFormatter(
    BSR[1]?.categories?.filter((obj) => obj.categoryValue === "REAL_ESTATE_ASSET_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "OtherLTA", value: FinancingHelper.bracketFormatter(
    BSR[1]?.categories?.filter((obj) => obj.categoryValue === "OTHER_LONG_TERM_ASSET_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "GrossFixA", value: FinancingHelper.bracketFormatter(
    BSR[1]?.categories?.filter((obj) => obj.categoryValue === "GROSS_FIXED_ASSETS_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "TotalFixA", value: FinancingHelper.bracketFormatter(BSR[1]?.totalAdjustedValue) })
  tokens.push({ name: "Goodwill", value: GWR?.formattedGoodwillAmount })
  tokens.push({ name: "GoodwillToAsset", value: GWR?.formattedGoodwillPercentageOfAssets })
  tokens.push({ name: "GoodwillToEqu", value: GWR?.formattedGoodwillPercentageOfEquity })
  tokens.push({ name: "TotalA", value: GWR?.formattedTotalAssets })
  tokens.push({ name: "AP", value: FinancingHelper.bracketFormatter(
    BSR[2]?.categories?.filter((obj) => obj.categoryValue === "ACCOUNTS_PAYABLE_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "LoansP", value: FinancingHelper.bracketFormatter(
    BSR[2]?.categories?.filter((obj) => obj.categoryValue === "LOANS_AND_NOTES_PAYABLE")[0]?.balance
  ) })
  tokens.push({ name: "OtherCL", value: FinancingHelper.bracketFormatter(
    BSR[2]?.categories?.filter((obj) => obj.categoryValue === "OTHER_CURRENT_LIABILITIES_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "TotalCL", value: FinancingHelper.bracketFormatter(BSR[2]?.totalAdjustedValue) })
  tokens.push({ name: "LTL", value: FinancingHelper.bracketFormatter(
    BSR[3]?.categories?.filter((obj) => obj.categoryValue === "LONG_TERM_LIABILITIES_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "OtherLTL", value: FinancingHelper.bracketFormatter(
    BSR[3]?.categories?.filter((obj) => obj.categoryValue === "OTHER_LONG_TERM_LIABILITY_CATEGORY")[0]?.balance
  ) })
  tokens.push({ name: "TotalLTL", value: FinancingHelper.bracketFormatter(BSR[3]?.totalAdjustedValue) })
  tokens.push({ name: "TotalL", value: GWR?.formattedTotalLiabilities })
  tokens.push({ name: "EquityValue", value: GWR?.formattedEquityValueOfBusiness })
  tokens.push({ name: "SSellerCash", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "CASH_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerSecurity", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "MARKETABLE_SECURITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerAR", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "ACCOUNTS_RECEIVABLE_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerInventory", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "INVENTORY_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerOtherCA", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "OTHER_CURRENT_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerTotalCA", value: FinancingHelper.bracketFormatter(SDR?.totalCurrentAssetsSellerKeeps) })
  tokens.push({ name: "SSellerRealEstate", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "REAL_ESTATE_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerOtherLTA", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerFixedA", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "GROSS_FIXED_ASSETS_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerTotalFA", value: FinancingHelper.bracketFormatter(SDR?.totalLongTermAssetsSellerKeeps) })
  tokens.push({ name: "SSellerTotalA", value: FinancingHelper.bracketFormatter(SDR?.totalAssetsSellerKeeps) })
  tokens.push({ name: "SSellerAP", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "ACCOUNTS_PAYABLE_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerLoansP", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "LOANS_AND_NOTES_PAYABLE")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerOtherCL", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "OTHER_CURRENT_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerTotalCL", value: FinancingHelper.bracketFormatter(SDR?.totalCurrentLiabilitiesSellerKeeps) })
  tokens.push({ name: "SSellerLTL", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "LONG_TERM_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerOtherLTL", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_LIABILITY_CATEGORY")?.map((obj) => obj.amount * (1 - obj.stockDealOwner))[0] || 0
  ) })
  tokens.push({ name: "SSellerTotalLTL", value: FinancingHelper.bracketFormatter(SDR?.totalLongTermLiabilitiesSellerKeeps) })
  tokens.push({ name: "SSellerTotalL", value: FinancingHelper.bracketFormatter(SDR?.totalLiabilitiesSellerKeeps) })
  tokens.push({ name: "SBuyerCash", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "CASH_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerSecurity", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "MARKETABLE_SECURITIES_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerAR", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "ACCOUNTS_RECEIVABLE_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerInventory", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "INVENTORY_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerOtherCA", value: FinancingHelper.bracketFormatter(
    SDR?.currentAssetsSplit?.filter((obj) => obj.value === "OTHER_CURRENT_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerTotalCA", value: FinancingHelper.bracketFormatter(SDR?.totalCurrentAssetsBuyerBuys) })
  tokens.push({ name: "SBuyerRealEstate", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "REAL_ESTATE_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerOtherLTA", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerFixedA", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "GROSS_FIXED_ASSETS_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerTotalFA", value: FinancingHelper.bracketFormatter(SDR?.totalLongTermAssetsBuyerBuys) })
  tokens.push({ name: "SBuyerGoodwill", value: FinancingHelper.bracketFormatter(GWR?.goodwillAmount) })
  tokens.push({ name: "SBuyerTotalA", value: FinancingHelper.bracketFormatter(
    SDR?.totalCurrentAssetsBuyerBuys + SDR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount
  ) })
  tokens.push({ name: "SBuyerAP", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "ACCOUNTS_PAYABLE_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerLoansP", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "LOANS_AND_NOTES_PAYABLE")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerOtherCL", value: FinancingHelper.bracketFormatter(
    SDR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "OTHER_CURRENT_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerTotalCL", value: FinancingHelper.bracketFormatter(SDR?.totalCurrentLiabilitiesBuyerBuys) })
  tokens.push({ name: "SBuyerLTL", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "LONG_TERM_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerOtherLTL", value: FinancingHelper.bracketFormatter(
    SDR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_LIABILITY_CATEGORY")?.map((obj) => obj.amount * obj.stockDealOwner)[0] || 0
  ) })
  tokens.push({ name: "SBuyerTotalLTL", value: FinancingHelper.bracketFormatter(SDR?.totalLongTermLiabilitiesBuyerBuys) })
  tokens.push({ name: "SBuyerTotalL", value: FinancingHelper.bracketFormatter(SDR?.totalLiabilitiesAssumedByBuyer) })
  tokens.push({ name: "SBuyerAsset", value: FinancingHelper.bracketFormatter(
    SDR?.totalCurrentAssetsBuyerBuys + SDR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount
  ) })
  tokens.push({ name: "SBuyerLia", value: FinancingHelper.bracketFormatter(SDR?.totalLiabilitiesAssumedByBuyer) })
  tokens.push({ name: "SEscrow", value: FinancingHelper.bracketFormatter(
    SDR?.totalCurrentAssetsBuyerBuys + SDR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount - SDR?.totalLiabilitiesAssumedByBuyer
  ) })
  tokens.push({ name: "SSellerAsset", value: FinancingHelper.bracketFormatter(SDR?.totalAssetsSellerKeeps) })
  tokens.push({ name: "SSellerLia", value: FinancingHelper.bracketFormatter(SDR?.totalLiabilitiesSellerKeeps) })
  tokens.push({ name: "SNetCash", value: FinancingHelper.bracketFormatter(
    SDR?.totalCurrentAssetsBuyerBuys +
    SDR?.totalLongTermAssetsBuyerBuys +
    GWR?.goodwillAmount -
    SDR?.totalLiabilitiesAssumedByBuyer +
    SDR?.totalAssetsSellerKeeps -
    SDR?.totalLiabilitiesSellerKeeps
  ) })
  tokens.push({ name: "ASellerCash", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "CASH_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerSecurity", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "MARKETABLE_SECURITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerAR", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "ACCOUNTS_RECEIVABLE_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerInventory", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "INVENTORY_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerOtherCA", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "OTHER_CURRENT_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerTotalCA", value: FinancingHelper.bracketFormatter(ADR?.totalCurrentAssetsSellerKeeps) })
  tokens.push({ name: "ASellerRealEstate", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "REAL_ESTATE_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerOtherLTA", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_ASSET_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerFixedA", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "GROSS_FIXED_ASSETS_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerTotalFA", value: FinancingHelper.bracketFormatter(ADR?.totalLongTermAssetsSellerKeeps) })
  tokens.push({ name: "ASellerTotalA", value: FinancingHelper.bracketFormatter(ADR?.totalAssetsSellerKeeps) })
  tokens.push({ name: "ASellerAP", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "ACCOUNTS_PAYABLE_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerLoansP", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "LOANS_AND_NOTES_PAYABLE")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerOtherCL", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "OTHER_CURRENT_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerTotalCL", value: FinancingHelper.bracketFormatter(ADR?.totalCurrentLiabilitiesSellerKeeps) })
  tokens.push({ name: "ASellerLTL", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "LONG_TERM_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerOtherLTL", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_LIABILITY_CATEGORY")?.map((obj) => obj.amount * (1 - obj.assetSaleDealOwner))[0] || 0
  ) })
  tokens.push({ name: "ASellerTotalLTL", value: FinancingHelper.bracketFormatter(ADR?.totalLongTermLiabilitiesSellerKeeps) })
  tokens.push({ name: "ASellerTotalL", value: FinancingHelper.bracketFormatter(ADR?.totalLiabilitiesSellerKeeps) })
  tokens.push({ name: "ABuyerCash", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "CASH_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerSecurity", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "MARKETABLE_SECURITIES_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerAR", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "ACCOUNTS_RECEIVABLE_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerInventory", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "INVENTORY_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerOtherCA", value: FinancingHelper.bracketFormatter(
    ADR?.currentAssetsSplit?.filter((obj) => obj.value === "OTHER_CURRENT_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerTotalCA", value: FinancingHelper.bracketFormatter(ADR?.totalCurrentAssetsBuyerBuys) })
  tokens.push({ name: "ABuyerRealEstate", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "REAL_ESTATE_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerOtherLTA", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_ASSET_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerFixedA", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "GROSS_FIXED_ASSETS_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerTotalFA", value: FinancingHelper.bracketFormatter(ADR?.totalLongTermAssetsBuyerBuys) })
  tokens.push({ name: "ABuyerGoodwill", value: FinancingHelper.bracketFormatter(GWR?.goodwillAmount) })
  tokens.push({ name: "ABuyerTotalA", value: FinancingHelper.bracketFormatter(
    ADR?.totalCurrentAssetsBuyerBuys + ADR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount
  ) })
  tokens.push({ name: "ABuyerAP", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "ACCOUNTS_PAYABLE_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerLoansP", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "LOANS_AND_NOTES_PAYABLE")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerOtherCL", value: FinancingHelper.bracketFormatter(
    ADR?.currentLiabilitiesSplit?.filter((obj) => obj.value === "OTHER_CURRENT_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerTotalCL", value: FinancingHelper.bracketFormatter(ADR?.totalCurrentLiabilitiesBuyerBuys) })
  tokens.push({ name: "ABuyerLTL", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "LONG_TERM_LIABILITIES_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerOtherLTL", value: FinancingHelper.bracketFormatter(
    ADR?.longTermAssetsSplit?.filter((obj) => obj.value === "OTHER_LONG_TERM_LIABILITY_CATEGORY")?.map((obj) => obj.amount * obj.assetSaleDealOwner)[0] || 0
  ) })
  tokens.push({ name: "ABuyerTotalLTL", value: FinancingHelper.bracketFormatter(ADR?.totalLongTermLiabilitiesBuyerBuys) })
  tokens.push({ name: "ABuyerTotalL", value: FinancingHelper.bracketFormatter(ADR?.totalLiabilitiesAssumedByBuyer) })
  tokens.push({ name: "ABuyerAsset", value: FinancingHelper.bracketFormatter(
    ADR?.totalCurrentAssetsBuyerBuys + ADR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount
  ) })
  tokens.push({ name: "ABuyerLia", value: FinancingHelper.bracketFormatter(ADR?.totalLiabilitiesAssumedByBuyer) })
  tokens.push({ name: "AEscrow", value: FinancingHelper.bracketFormatter(
    ADR?.totalCurrentAssetsBuyerBuys + ADR?.totalLongTermAssetsBuyerBuys + GWR?.goodwillAmount - ADR?.totalLiabilitiesAssumedByBuyer
  ) })
  tokens.push({ name: "ASellerAsset", value: FinancingHelper.bracketFormatter(ADR?.totalAssetsSellerKeeps) })
  tokens.push({ name: "ASellerLia", value: FinancingHelper.bracketFormatter(ADR?.totalLiabilitiesSellerKeeps) })
  tokens.push({ name: "ANetCash", value: FinancingHelper.bracketFormatter(
    ADR?.totalCurrentAssetsBuyerBuys +
    ADR?.totalLongTermAssetsBuyerBuys +
    GWR?.goodwillAmount -
    ADR?.totalLiabilitiesAssumedByBuyer +
    ADR?.totalAssetsSellerKeeps -
    ADR?.totalLiabilitiesSellerKeeps
  ) })
  tokens.push({ name: "DealType", value: dealType === "STOCK" ? "Stock Sale Deal" : "Asset Sale Deal" })
  tokens.push({ name: "PWBrokerFee", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.brokerageFees) })
  tokens.push({ name: "PWFilings", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.filingsAndRecordings) })
  tokens.push({ name: "PWAttoFee", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.attorneyFees) })
  tokens.push({ name: "PWAccFee", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.accountantFees) })
  tokens.push({ name: "PWEscrowFee", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.escrowFees) })
  const SECustomArray = (dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.customFields;
  SECustomArray.map((item, index) => {
    tokens.push({name: `PWSEName${index + 1}`, value: item?.name});
    tokens.push({name: `PWSEItem${index + 1}`, value: FinancingHelper.bracketFormatter(item?.value)});
  })
  tokens.push({ name: "PWPayPayables", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW).accountsPayable?.payrollPayables) })
  tokens.push({ name: "PWSalPayables", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW).accountsPayable?.salesTaxPayables) })
  const APCustomArray = (dealType === "STOCK" ? SPW : APW)?.accountsPayable?.customFields;
  APCustomArray.map((item, index) => {
    tokens.push({name: `PWAPName${index + 1}`, value: item?.name});
    tokens.push({name: `PWAPItem${index + 1}`, value: FinancingHelper.bracketFormatter(item?.value)});
  })
  const LTDCustomArray = dealType === "STOCK" ? [...SPW?.longTermDebtNotAssumedByBuyer?.longTermDebt.map(item => ({name: item.name, value: item.amount})), ...SPW?.longTermDebtNotAssumedByBuyer?.customFields]
    : [...APW?.longTermDebtsNotAssumedByPurchaser?.longTermDebts.map(item => ({name: item.name, value: item.amount})), ...APW?.longTermDebtsNotAssumedByPurchaser?.customFields];
  LTDCustomArray.map((item, index) => {
    tokens.push({name: `PWLTDName${index + 1}`, value: item?.name});
    tokens.push({name: `PWLTDItem${index + 1}`, value: FinancingHelper.bracketFormatter(item?.value)});
  })
  tokens.push({ name: "PWEquRepair", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.otherExpensesNeededToClose?.equipmentRepair) })
  tokens.push({ name: "PWEquReplace", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.otherExpensesNeededToClose?.equipmentReplace) })
  tokens.push({ name: "PWCompliance", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.otherExpensesNeededToClose?.complianceWithGovernmentalAgencies) })
  const OENCustomArray = (dealType === "STOCK" ? SPW : APW)?.otherExpensesNeededToClose?.customFields;
  OENCustomArray.map((item, index) => {
    tokens.push({name: `PWOENName${index + 1}`, value: item?.name});
    tokens.push({name: `PWOENItem${index + 1}`, value: FinancingHelper.bracketFormatter(item?.value)});
  })
  tokens.push({ name: "PWReturn", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.returnOfLeaseDeposit) })
  tokens.push({ name: "PWPetty", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.pettyCash) })
  tokens.push({ name: "PWRent", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.proratedRent) })
  tokens.push({ name: "PWDeposit", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.utilityDeposit) })
  tokens.push({ name: "PWTax", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.proratedTaxes) })
  tokens.push({ name: "PWRebate", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.rebateForPrepaidExpenses) })
  const CACustomArray = dealType === "STOCK" ? [...SPW?.totalCurrentAssetsKeptBySeller?.currentAssets.map(item => ({name: item.name, value: item.amount})), ...SPW?.totalCurrentAssetsKeptBySeller?.customFields]
    : [...APW?.totalCurrentAssetsKeptBySeller?.currentAssets.map(item => ({name: item.name, value: item.amount})), ...APW?.totalCurrentAssetsKeptBySeller?.customFields];
  CACustomArray.map((item, index) => {
    tokens.push({name: `PWCAName${index + 1}`, value: item?.name});
    tokens.push({name: `PWCAItem${index + 1}`, value: FinancingHelper.bracketFormatter(item?.value)});
  })
  tokens.push({ name: "PWCash", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.cashDownPayment) })
  tokens.push({ name: "PWSellingExp", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.sellingExpenses?.totalSellingExpenses) })
  tokens.push({ name: "PWAP", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.accountsPayable?.totalAccountsPayable) })
  tokens.push({ name: "PWLTDebt", value: FinancingHelper.bracketFormatter(
    dealType === "STOCK" ? SPW?.longTermDebtNotAssumedByBuyer?.totalLongTermDebt : APW?.longTermDebtsNotAssumedByPurchaser?.totalLongTermDebtNotAssumedByPurchaser
  ) })
  tokens.push({ name: "PWOtherExp", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.otherExpensesNeededToClose?.totalOtherExpensesNeededToClose) })
  tokens.push({ name: "PWTotalExp", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalSalesAndOtherExpenses) })
  tokens.push({ name: "PWNetCash", value: FinancingHelper.bracketFormatter(
    dealType === "STOCK" ? SPW?.netCashAfterLiabilitiesAndSellingExpenses : APW?.netCashAfterSellingAndExpenses
  ) })
  tokens.push({ name: "PWTotalCA", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.totalCurrentAssetsKeptBySeller?.totalCurrentAssetsKeptBySeller) })
  tokens.push({ name: "PWTotal", value: FinancingHelper.bracketFormatter((dealType === "STOCK" ? SPW : APW)?.estimatedTotalNetCashProceedsToSeller) })
  tokens.push({ name: "TransactionExpense", value: FinancingHelper.bracketFormatter(
    (dealType === "STOCK" ? stockPurchaserAcquisitionExpenses : assetPurchaserAcquisitionExpenses)?.generalAcquisition?.totalClosingCostsToBePaidByPurchaser
  ) })
  tokens.push({ name: "BusinessExpense", value: FinancingHelper.bracketFormatter(
    (dealType === "STOCK" ? stockPurchaserAcquisitionExpenses : assetPurchaserAcquisitionExpenses)?.acquisitionLicensesTaxesPermitsAndFees?.totalAdditionalPurchaserCashPayoutRequirements
  ) })
  tokens.push({ name: "AssetsNeeded", value: FinancingHelper.bracketFormatter(
    (dealType === "STOCK" ? stockPurchaserAcquisitionExpenses : assetPurchaserAcquisitionExpenses)?.prePaidAssetsNeeded?.totalAdditionalPurchaserCashPayoutForAssets
  ) })
  tokens.push({ name: "WCNeeded", value: FinancingHelper.bracketFormatter(
    (dealType === "STOCK" ? stockPurchaserAcquisitionExpenses : assetPurchaserAcquisitionExpenses)?.workingCapitalNeededAtClosing?.totalCashPaymentForAllAdditionalAssetsToBePurchased
  ) })
  tokens.push({ name: "TotalNeeded", value: FinancingHelper.bracketFormatter(
    (dealType === "STOCK" ? stockPurchaserAcquisitionExpenses : assetPurchaserAcquisitionExpenses)?.additionalCapitalNeededAtClosing
  ) })
  tokens.push({ name: "MinCashBal", value: FinancingHelper.bracketFormatter(currentLBODashboard?.minimumCashBalance) })
  tokens.push({ name: "TaxRate", value: currentLBODashboard?.taxRate + "%" })
  tokens.push({ name: "Shares", value: currentLBODashboard?.targetSharesOutstanding + " Shares" })
  tokens.push({ name: "SharePrice", value: FinancingHelper.bracketFormatter(currentLBODashboard?.targetSharePrice) })
  tokens.push({ name: "TransactionCost", value: FinancingHelper.bracketFormatter(currentLBODashboard?.transactionCosts) })
  tokens.push({ name: "RestructureCharge", value: FinancingHelper.bracketFormatter(currentLBODashboard?.restructuringCharges) })
  tokens.push({ name: "Premium", value: currentLBODashboard?.premiumOrDiscount + "%" })
  tokens.push({ name: "ShareOfferPrice", value: FinancingHelper.bracketFormatter(currentLBODashboard?.offerPricePerShare) })
  tokens.push({ name: "PurchasePrice", value: FinancingHelper.bracketFormatter(currentLBODashboard?.purchasePrice) })
  tokens.push({ name: "DebtAssumed", value: FinancingHelper.bracketFormatter(currentLBODashboard?.debtAssumed) })
  tokens.push({ name: "BalanceOfEquity", value: FinancingHelper.bracketFormatter(currentLBODashboard?.balanceOfEquity) })
  tokens.push({ name: "EarnoutTarget", value: currentLBODashboard?.earnoutTarget })
  tokens.push({ name: "TargetAmount", value: FinancingHelper.bracketFormatter(currentLBODashboard?.earnoutTargetAmount) })
  tokens.push({ name: "EarnoutYears", value: currentLBODashboard?.earnoutYears + " Years" })
  tokens.push({ name: "Compensation", value: FinancingHelper.bracketFormatter(currentLBODashboard?.compensation) })
  tokens.push({ name: "TotalEarnout", value: FinancingHelper.bracketFormatter(currentLBODashboard?.earnoutTargetTotal) })
  tokens.push({ name: "ConsultingFee", value: FinancingHelper.bracketFormatter(currentLBODashboard?.consultingFee) })
  tokens.push({ name: "ConsultingMonths", value: currentLBODashboard?.consultingPeriod + " Months" })
  tokens.push({ name: "TotalConsulting", value: FinancingHelper.bracketFormatter(currentLBODashboard?.consultingPaymentTotal) })
  tokens.push({ name: "AmortizedOver", value: currentLBODashboard?.amortizedOver + " Months" })
  tokens.push({ name: "GracePeriod", value: currentLBODashboard?.gracePeriod + " Months" })
  tokens.push({ name: "BalloonAfter", value: currentLBODashboard?.balloonPaymentAfter + " Months" })
  tokens.push({ name: "CashSweep", value: currentLBODashboard?.cashSweepPortion + "%" })
  tokens.push({ name: "ExitYear", value: currentLBODashboard?.exitYear + " Years" })
  tokens.push({ name: "LTGrowthRate", value: currentLBODashboard?.longTermGrowthRate + "%" })
  tokens.push({ name: "FixedSynergiesRate", value: currentLBODashboard?.fixedSynergiesRate + "%" })
  tokens.push({ name: "DividendsPercent", value: currentLBODashboard?.dividendsPercent + "%" })
  tokens.push({ name: "LOCLimit", value: FinancingHelper.bracketFormatter(currentLBODashboard?.capacity) })
  tokens.push({ name: "SOFRCurrent", value: currentLBODashboard?.SOFRCurrent + " bps" })
  tokens.push({ name: "SOFRStep", value: currentLBODashboard?.SOFRStep + " bps" })
  tokens.push({ name: "PIKStep", value: currentLBODashboard?.PIKStep + " bps" })
  tokens.push({ name: "MIRRReinvestRate", value: currentLBODashboard?.reinvestmentRate + "%" })
  dealDCFAnalysis?.FCFtoFirm?.map((item, index) => { tokens.push({
    name: `FCFF${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.synergiesAndGrowth?.map((item, index) => { tokens.push({
    name: `Syne${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.totalFCF?.map((item, index) => { tokens.push({
    name: `TCFF${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.earnoutArray?.map((item, index) => { tokens.push({
    name: `EC${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.consultingArray?.map((item, index) => { tokens.push({
    name: `CFee${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.totalDebtTerm?.map((item, index) => { tokens.push({
    name: `TDT${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFAnalysis?.FCFAvailable?.map((item, index) => { tokens.push({
    name: `FCFA${index + 1}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  debtPanel?.debtArray?.map((debt, index) => {
    tokens.push({ name: `Debt${index + 1}`, value: debt?.name })
    tokens.push({ name: `DAmount${index + 1}`, value: FinancingHelper.bracketFormatter(debt?.value) })
    tokens.push({ name: `DPer${index + 1}`, value: debt?.percent?.toFixed(2) + "%" })
    if(!debt?.name?.includes("Equity")) {
      tokens.push({ name: `DFee${index + 1}`, value: debt?.fee + "%" })
      tokens.push({ name: `IType${index + 1}`, value: debt?.interestType })
      tokens.push({ name: `DIR${index + 1}`, value: debt?.rate + (debt?.interestType === "Fixed" ? "%" : "% + SOFR") })
      if(debt.name !== "Line of Credit") {
        tokens.push({ name: `DRePe${index + 1}`, value: debt?.period + (debt.name === "Owner Financing" ? " Mos" : " Years") })
        tokens.push({ name: `DMora${index + 1}`, value: debt?.moratorium + (debt.name === "Owner Financing" ? " Mos" : " Years") })
        tokens.push({ name: `DTT${index + 1}`, value: debt?.totalTerm + (debt.name === "Owner Financing" ? " Mos" : " Years") })
        tokens.push({ name: `DSY${index + 1}`, value: "Year " + debt?.startYear })
        debt?.interestType === "Floating" && tokens.push({ name: `Fl${index + 1}`, value: debt?.floor + "%" })
        tokens.push({ name: `Sw${index + 1}`, value: debt?.sweep + "%" })
        debt.name !== "Owner Financing" && tokens.push({ name: `DTR${index + 1}`, value: debt?.repayType })
        debt.repayType === "PIK" && tokens.push({ name: `DPI${index + 1}`, value: debt?.pikRate + "%" })
      }
    }
  })
  tokens.push({ name: "DTotal", value: FinancingHelper.bracketFormatter(debtPanel?.debtTotal) })
  tokens.push({ name: "FinancingFee", value: FinancingHelper.bracketFormatter(debtPanel?.feesTotal) })
  tokens.push({ name: "CashOnBS", value: FinancingHelper.bracketFormatter(debtPanel?.cash) })
  tokens.push({ name: "FundTotal", value: FinancingHelper.bracketFormatter(debtPanel?.fundsTotal) })
  tokens.push({ name: "FundPurchase", value: FinancingHelper.bracketFormatter(debtPanel?.purchasePrice) })
  tokens.push({ name: "CostWCRC", value: FinancingHelper.bracketFormatter(debtPanel?.transCoAndRest) })
  tokens.push({ name: "DebtIncluding", value: FinancingHelper.bracketFormatter(debtPanel?.debtInclude) })
  tokens.push({ name: "DebtExcluding", value: FinancingHelper.bracketFormatter(debtPanel?.debtExclude) })
  tokens.push({ name: "DealOfferEquity", value: FinancingHelper.bracketFormatter(debtPanel?.sponsorEquity) })
  tokens.push({ name: "RatioIncluding", value: debtPanel?.ratioInclude?.toFixed(2) + "%" })
  tokens.push({ name: "RatioExcluding", value: debtPanel?.ratioExclude?.toFixed(2) + "%" })
  dealDCFValuation?.yearsFormatted?.map((item, index) => { tokens.push({
    name: `DOY${index}`,
    value: item.slice(-4)   //just get year
  }) })
  dealDCFValuation?.FCFE?.map((item, index) => { tokens.push({
    name: `FCFE${index}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFValuation?.terminalValue?.map((item, index) => { !item?.disabled && tokens.push({
    name: `TV${index}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFValuation?.totalFCFE?.map((item, index) => { tokens.push({
    name: `TFCF${index}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFValuation?.cumulativeCashFlows?.map((item, index) => { tokens.push({
    name: `CCF${index}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFValuation?.PVFactor?.map((item, index) => { tokens.push({
    name: `PVF${index}`,
    value: item?.value?.toFixed(2)
  }) })
  dealDCFValuation?.NPVfromFirstPrincipal?.map((item, index) => { tokens.push({
    name: `NPV${index}`,
    value: FinancingHelper.bracketIntegerFormatter(item?.value)
  }) })
  dealDCFValuation?.debtToEBITDA?.map((item, index) => { tokens.push({
    name: `DTEA${index}`,
    value: item?.value?.toFixed(2) + "x"
  }) })
  dealDCFValuation?.debtToEBIT?.map((item, index) => { tokens.push({
    name: `DTE${index}`,
    value: item?.value?.toFixed(2) + "x"
  }) })
  dealDCFValuation?.EBITDAToRepayment?.map((item, index) => { tokens.push({
    name: `EATR${index}`,
    value: item?.disabled ? "-" : item?.value?.toFixed(2) + "x"
  }) })
  dealDCFValuation?.EBITToRepayment?.map((item, index) => { tokens.push({
    name: `ETR${index}`,
    value: item?.disabled ? "-" : item?.value?.toFixed(2) + "x"
  }) })
  tokens.push({ name: "IRR3", value: dealDCFValuation?.debtMetrics?.lineOfCredit?.IRR?.toFixed(2) + "%" })
  tokens.push({ name: "EM3", value: dealDCFValuation?.debtMetrics?.lineOfCredit?.EM?.toFixed(2) + "x" })
  tokens.push({ name: "IRR4", value: dealDCFValuation?.debtMetrics?.ownerFinancing?.IRR?.toFixed(2) + "%" })
  tokens.push({ name: "EM4", value: dealDCFValuation?.debtMetrics?.ownerFinancing?.EM?.toFixed(2) + "x" })
  dealDCFValuation?.debtMetrics?.otherDebtsArray?.map((debt, index) => {
    tokens.push({ name: `IRR${index + 5}`, value: debt?.IRR?.toFixed(2) + "%" })
    tokens.push({ name: `EM${index + 5}`, value: debt?.EM?.toFixed(2) + "x" })
  })
  tokens.push({ name: "XNPV", value: FinancingHelper.bracketFormatter(dealDCFValuation?.enterpriseValue) })
  tokens.push({ name: "XIRR", value: dealDCFValuation?.XIRR?.toFixed(2) + "%" })
  tokens.push({ name: "MIRR", value: dealDCFValuation?.MIRR?.toFixed(2) + "%" })
  tokens.push({ name: "EMulti", value: dealDCFValuation?.cashOnReturn?.toFixed(2) + "x" })
  tokens.push({ name: "TValuePercent", value: dealDCFValuation?.percentTV?.toFixed(2) + "%" })
  tokens.push({ name: "CashflowPercent", value: dealDCFValuation?.percentAC?.toFixed(2) + "%" })
  tokens.push({ name: "PaybackPeriod", value: dealDCFValuation?.payback?.toFixed(2) + " Years" })
  tokens.push({ name: "ProfitIndex", value: dealDCFValuation?.profitability?.toFixed(2) + "x" })
  tokens.push({ name: "SummaryPurchasePrice", value: FinancingHelper.bracketFormatter(currentLBODashboard?.summaryPurchasePrice) })
  tokens.push({ name: "PercentPaid", value: currentLBODashboard?.summaryPercentPaid?.toFixed(2) + "%" })
  tokens.push({ name: "DownPaymentClosing", value: FinancingHelper.bracketFormatter(currentLBODashboard?.summaryDownPayment) })
  tokens.push({ name: "Financing", value: FinancingHelper.bracketFormatter(currentLBODashboard?.summaryFinancing) })
  tokens.push({ name: "MonthlyPayments", value: FinancingHelper.bracketFormatter(debtPanel?.debtAndCashEngine?.ownerFinancing?.monthlyPayments) })
  tokens.push({ name: "BalloonPayment", value: FinancingHelper.bracketFormatter(debtPanel?.debtAndCashEngine?.ownerFinancing?.balloonPayment) })
  tokens.push({ name: "SweepTotal", value: FinancingHelper.bracketFormatter(debtPanel?.debtAndCashEngine?.ownerFinancing?.cashSweepTotal) })
  tokens.push({ name: "InterestToSeller", value: FinancingHelper.bracketFormatter(debtPanel?.debtAndCashEngine?.ownerFinancing?.totalInterest) })
  tokens.push({ name: "PaymentsToSeller", value: FinancingHelper.bracketFormatter(
    currentLBODashboard?.summaryFinancing +
    debtPanel?.debtAndCashEngine?.ownerFinancing?.totalInterest +
    currentLBODashboard?.earnoutTargetTotal +
    currentLBODashboard?.consultingPaymentTotal
  ) })
  tokens.push({ name: "DealConsideration", value: FinancingHelper.bracketFormatter(
    currentLBODashboard?.summaryBalanceOfEquity +
    debtPanel?.debtAndCashEngine?.ownerFinancing?.totalInterest +
    currentLBODashboard?.earnoutTargetTotal +
    currentLBODashboard?.consultingPaymentTotal
  ) })
  tokens.push({ name: "BasisType", value: assumptions?.basis === "IRR" ? "IRR" : (assumptions?.basis === "COC" ? "Cash on Cash" : "Multiple") })
  tokens.push({ name: "ProceedsDistriputed", value: assumptions?.proceeds === "PR" ? "Pro Rata" : "Per Preference Schedule" })
  tokens.push({ name: "WACC", value: assumptions?.financeRate + "%" })
  tokens.push({ name: "MIRRReinvestRate", value: assumptions?.reinvestRate + "%" })
  tokens.push({ name: "ClosingFees", value: FinancingHelper.bracketFormatter(assumptions?.closingFees) })
  tokens.push({ name: "AdditionalDebt", value: FinancingHelper.bracketFormatter(assumptions?.additionalDebt) })
  tokens.push({ name: "RolloverEquity", value: FinancingHelper.bracketFormatter(assumptions?.rolloverEquity) })
  tokens.push({ name: "TotalEquity", value: FinancingHelper.bracketFormatter(assumptions?.totalEquity) })
  tokens.push({ name: "WExitYear", value: assumptions?.exitYear + " Years" })
  tokens.push({ name: "ExitProceeds", value: FinancingHelper.bracketFormatter(assumptions?.exitProceeds) })
  tokens.push({ name: "FeePercent", value: assumptions?.exitFeesPercent + "%" })
  tokens.push({ name: "FeeAmount", value: FinancingHelper.bracketFormatter(assumptions?.exitFees) })
  tokens.push({ name: "OtherExitFee", value: FinancingHelper.bracketFormatter(assumptions?.otherExitFees) })
  tokens.push({ name: "GrossProceeds", value: FinancingHelper.bracketFormatter(assumptions?.grossProceeds) })
  tokens.push({ name: "OutstandingDebt", value: FinancingHelper.bracketFormatter(assumptions?.outstandingDebt) })
  tokens.push({ name: "NetProceeds", value: FinancingHelper.bracketFormatter(assumptions?.netProceeds) })
  assumptions?.freeCashFlow?.map((item, index) => {
    tokens.push({ name: `WFCF${index + 1}`, value: FinancingHelper.bracketIntegerFormatter(item?.value) })
  })
  tokens.push({ name: "GPCash", value: FinancingHelper.bracketFormatter(assumptions?.GPContributionAmount) })
  tokens.push({ name: "LPCash", value: FinancingHelper.bracketFormatter(assumptions?.LPContributionAmount) })
  tokens.push({ name: "GPCashPercent", value: assumptions?.GPContributionPercent?.toFixed(2) + "%" })
  tokens.push({ name: "LPCashPercent", value: assumptions?.LPContributionPercent?.toFixed(2) + "%" })
  tokens.push({ name: "CMPercent", value: assumptions?.CMOwnershipPercent?.toFixed(2) + "%" })
  tokens.push({ name: "GPPercent", value: assumptions?.GPOwnershipPercent?.toFixed(2) + "%" })
  tokens.push({ name: "LPPercent", value: assumptions?.LPOwnershipPercent?.toFixed(2) + "%" })
  tokens.push({ name: "GPCloseFee", value: FinancingHelper.bracketFormatter(assumptions?.GPClosingFeeAmount) })
  tokens.push({ name: "LPCloseFee", value: FinancingHelper.bracketFormatter(assumptions?.LPClosingFeeAmount) })
  tokens.push({ name: "GPManageFeePer", value: assumptions?.GPManagementFeePercent + "%" })
  tokens.push({ name: "LPManageFeePer", value: assumptions?.LPManagementFeePercent + "%" })
  tokens.push({ name: "GPManageFee", value: FinancingHelper.bracketFormatter(assumptions?.GPManagementFeeAmount) })
  tokens.push({ name: "LPManageFee", value: FinancingHelper.bracketFormatter(assumptions?.LPManagementFeeAmount) })
  tokens.push({ name: "GPAdminFeePer", value: assumptions?.GPAdministrativeFeePercent + "%" })
  tokens.push({ name: "LPAdminFeePer", value: assumptions?.LPAdministrativeFeePercent + "%" })
  tokens.push({ name: "GPAdminFee", value: FinancingHelper.bracketFormatter(assumptions?.GPAdministrativeFeeAmount) })
  tokens.push({ name: "LPAdminFee", value: FinancingHelper.bracketFormatter(assumptions?.LPAdministrativeFeeAmount) })
  tokens.push({ name: "GPDirectFeePer", value: assumptions?.GPDirectorFeePercent + "%" })
  tokens.push({ name: "LPDirectFeePer", value: assumptions?.LPDirectorFeePercent + "%" })
  tokens.push({ name: "GPDirectFee", value: FinancingHelper.bracketFormatter(assumptions?.GPDirectorFeeAmount) })
  tokens.push({ name: "LPDirectFee", value: FinancingHelper.bracketFormatter(assumptions?.LPDirectorFeeAmount) })
  tokens.push({ name: "GPPlaceFeePer", value: assumptions?.GPPlacementFeePercent + "%" })
  tokens.push({ name: "LPPlaceFeePer", value: assumptions?.LPPlacementFeePercent + "%" })
  tokens.push({ name: "GPPlaceFee", value: FinancingHelper.bracketFormatter(assumptions?.GPPlacementFeeAmount) })
  tokens.push({ name: "LPPlaceFee", value: FinancingHelper.bracketFormatter(assumptions?.LPPlacementFeeAmount) })
  tokens.push({ name: "GPTotalFee", value: FinancingHelper.bracketFormatter(assumptions?.GPTotalAnnualFees) })
  tokens.push({ name: "LPTotalFee", value: FinancingHelper.bracketFormatter(assumptions?.LPTotalAnnualFees) })
  if(assumptions?.proceeds === "PPS") {
    assumptions?.schedule?.map((schedule, index) => {
      tokens.push({ name: `TranProv${index + 1}`, value: schedule?.tranche ? "Yes" : "-" })
      if(schedule?.tranche) {
        tokens.push({ name: `Tran${index + 1}`, value: `Tranche ${index + 1}` })
        tokens.push({ name: `Hurdle${index + 1}`, value: schedule?.hurdle + "%" })
        assumptions?.basis === "MULTI" && tokens.push({ name: `MultiHurdle${index + 1}`, value: schedule?.multiHurdle + "x" })
        tokens.push({ name: `GPPro${index + 1}`, value: schedule?.promote + "%" })
        tokens.push({ name: `GPSplit${index + 1}`, value: schedule?.trancheGPSplit.toFixed(2) + "%" })
        tokens.push({ name: `LPSplit${index + 1}`, value: schedule?.trancheLPSplit.toFixed(2) + "%" })
        tokens.push({ name: `CUProv${index + 1}`, value: schedule?.catchUp ? "Yes" : "No" })
        if(schedule?.catchUp) {
          tokens.push({ name: `CUPer${index + 1}`, value: schedule?.catchUpPercent + "%" })
          tokens.push({ name: `CUGPSp${index + 1}`, value: schedule?.catchUpGPSplit + "%" })
          tokens.push({ name: `CULPSp${index + 1}`, value: schedule?.catchUpLPSplit + "%" })
        }
      }
    })
    if(assumptions?.clawback) tokens.push({ name: "ClawbackEligibility", value: "LPs are eligible for a clawback." })
    else tokens.push({ name: "ClawbackEligibility", value: "LPs are not eligible for a clawback." })
  }
  assumptions?.totalCashFlow?.cashFlow?.LP?.map((item, index) => {
    tokens.push({ name: `LPCF${index}`, value: FinancingHelper.bracketIntegerFormatter(item?.value) })
  })
  assumptions?.totalCashFlow?.cashFlow?.GP?.map((item, index) => {
    tokens.push({ name: `GPCF${index}`, value: FinancingHelper.bracketIntegerFormatter(item?.value) })
  })
  assumptions?.totalCashFlow?.cashFlow?.CM?.map((item, index) => {
    tokens.push({ name: `CMCF${index}`, value: FinancingHelper.bracketIntegerFormatter(item?.value) })
  })
  assumptions?.totalCashFlow?.cashFlow?.total?.map((item, index) => {
    tokens.push({ name: `TtCF${index}`, value: FinancingHelper.bracketIntegerFormatter(item?.value) })
  })
  tokens.push({ name: "TotInvest", value: FinancingHelper.bracketFormatter(assumptions?.totalEquity) })
  tokens.push({ name: "LPProceed", value: FinancingHelper.bracketFormatter(assumptions?.totalCashFlow?.totalProceeds?.LP) })
  tokens.push({ name: "GPProceed", value: FinancingHelper.bracketFormatter(assumptions?.totalCashFlow?.totalProceeds?.GP) })
  tokens.push({ name: "CMProceed", value: FinancingHelper.bracketFormatter(assumptions?.totalCashFlow?.totalProceeds?.CM) })
  tokens.push({ name: "TotProceed", value: FinancingHelper.bracketFormatter(assumptions?.totalCashFlow?.totalProceeds?.total) })
  tokens.push({ name: "LPIRR", value: assumptions?.totalCashFlow?.IRR?.LP?.toFixed(2) + "%" })
  tokens.push({ name: "GPIRR", value: assumptions?.totalCashFlow?.IRR?.GP?.toFixed(2) + "%" })
  tokens.push({ name: "TotIRR", value: assumptions?.totalCashFlow?.IRR?.total?.toFixed(2) + "%" })
  tokens.push({ name: "LPEM", value: assumptions?.totalCashFlow?.EM?.LP?.toFixed(2) + "x" })
  tokens.push({ name: "GPEM", value: assumptions?.totalCashFlow?.EM?.GP?.toFixed(2) + "x" })
  tokens.push({ name: "TotEM", value: assumptions?.totalCashFlow?.EM?.total?.toFixed(2) + "x" })
  tokens.push({ name: "LPMIRR", value: assumptions?.totalCashFlow?.MIRR?.toFixed(2) + "%" })
  tokens.push({ name: "GPPromote", value: FinancingHelper.bracketFormatter(assumptions?.scheduleEngine?.GPPromote) })
  tokens.push({ name: "BusinessName", value: generalInfo?.name })
  tokens.push({ name: "BusinessAddress", value: generalInfo?.address })
  tokens.push({ name: "BusinessCity", value: generalInfo?.city })
  tokens.push({ name: "BusinessState", value: generalInfo?.state })
  tokens.push({ name: "BusinessZip", value: generalInfo?.zipCode })
  tokens.push({ name: "BusinessCountry", value: generalInfo?.country })
  tokens.push({ name: "BusinessWebsite", value: generalInfo?.website })
  tokens.push({ name: "BusinessDUNS", value: generalInfo?.dunsNumber })
  tokens.push({ name: "BusinessPNAICS", value: generalInfo?.pNAICS })
  tokens.push({ name: "BusinessPSIC", value: generalInfo?.pSIC })
  tokens.push({ name: "BusinessPISIC", value: generalInfo?.pISIC })
  tokens.push({ name: "BusinessSNAICS", value: generalInfo?.sNAICS })
  tokens.push({ name: "BusinessSSIC", value: generalInfo?.sSIC })
  tokens.push({ name: "BusinessSISIC", value: generalInfo?.sISIC })
  tokens.push({ name: "BusinessOverview", value: generalInfo?.overview })
  tokens.push({ name: "IndustryReport", value: generalInfo?.industry })
  tokens.push({ name: "EconomicsReport", value: generalInfo?.economics })
  tokens.push({ name: "BusinessFTE", value: businessDetails?.estimatedEmployees })
  tokens.push({ name: "BusinessForm", value: businessDetails?.organizationalForm })
  tokens.push({ name: "BusinessFormState", value: businessDetails?.stateOfOrganizationalForm })
  tokens.push({ name: "ContactName", value: businessDetails?.retainedBy?.contactName })
  tokens.push({ name: "PositionTitle", value: businessDetails?.retainedBy?.positionTitle })
  tokens.push({ name: "ContactEmail", value: businessDetails?.retainedBy?.contactEmail })
  tokens.push({ name: "ContactPhone", value: businessDetails?.retainedBy?.contactPhone })
  businessDetails?.capTable?.map((row, index) => {
    tokens.push({ name: `TName${index + 1}`, value: row.shareholderNames })
    tokens.push({ name: `TTitle${index + 1}`, value: row.positionOrTitle })
    tokens.push({ name: `TDuty${index + 1}`, value: row.descriptionOfDuties })
  })
  tokens.push({ name: "ValuationPurpose", value: valuationInfo?.purposeOfValuation })
  tokens.push({ name: "IntendedUser", value: valuationInfo?.intendedUser })
  tokens.push({ name: "SubjectInterest", value: valuationInfo?.subjectInterest + "%" })
  tokens.push({ name: "FormOfReport", value: valuationInfo?.formOfReport })
  tokens.push({ name: "TypeOfReport", value: valuationInfo?.typeOfReport })
  tokens.push({ name: "StandardOfValue", value: valuationInfo?.standardOfValue })
  tokens.push({ name: "PremiseOfValue", value: valuationInfo?.premiseOfValue })
  tokens.push({ name: "ControllingInterest", value: valuationInfo?.controllingInterest })
  tokens.push({ name: "Marketability", value: valuationInfo?.marketability })
  tokens.push({ name: "LackofControl", value: valuationInfo?.discountForLackOfControl })
  tokens.push({ name: "LackofMarketability", value: valuationInfo?.discountForLackOfMarketability })
  tokens.push({ name: "ReportDate", value: valuationInfo?.reportDate })
  tokens.push({ name: "Name", value: valuationInfo?.analystName })
  tokens.push({ name: "Title", value: valuationInfo?.title })
  tokens.push({ name: "Email", value: valuationInfo?.email })
  tokens.push({ name: "Phone", value: valuationInfo?.phone })
  tokens.push({ name: "Credentials", value: valuationInfo?.credentials })
  tokens.push({ name: "ResumeNote", value: valuationInfo?.resumeNote })
  let conflictArray = [];
  if(disclosure?.conflictOneChecked) conflictArray.push("The Valuation Analyst did not know or have a personal relationship with Client prior to engaging in this valuation assignment.");
  if(disclosure?.conflictTwoChecked) conflictArray.push("The Valuation Analyst did not have any personal knowledge about the business, which would cause our valuation firm to have information that another firm could not readily have.");
  if(disclosure?.conflictThreeChecked) conflictArray.push("The Valuation Analyst is not currently engaged and will not be providing any further ongoing services to include:\nBusiness Brokering\nManagement Consulting\nTax Preparation\nBookkeeping\nOther Consulting Services");
  if(disclosure?.conflictFourChecked) conflictArray.push("The Valuation Analyst has not conducted a previous valuation of the clients within a three-year period.");
  if(disclosure?.conflictFiveChecked) conflictArray.push("The Valuation Analyst's integrity of independence was not compromised in generating the Conclusion of Value.");
  conflictArray.map((item, index) => { tokens.push({
    name: `ConflictCheck${index + 1}`,
    value: item
  }) })
  if(disclosure?.contingencyChecked) tokens.push({
    name: "Disclosure",
    value: "Only a fee was paid to the Valuation Analyst for their work on this valuation report. This charge was not contingent on the Conclusion of Value nor was it based on it."
  })
  if(disclosure?.interestChecked) tokens.push({
    name: "Statement",
    value: "The Valuation Analyst does not have any present interests in the company that is being valued."
  })
  let statementArray = [];
  let otherReportArray = [];
  disclosure?.typesUsed && disclosure?.typesUsed.map(item =>
    item.isCustom ? (
      item.checked && otherReportArray.push(item.label)
    ) : (
      item.checked && statementArray.push(item.label)
    )
  )
  statementArray.map((item, index) => { tokens.push({
    name: `FinReport${index + 1}`,
    value: item
  }) })
  otherReportArray.map((item, index) => { tokens.push({
    name: `OtherReport${index + 1}`,
    value: item
  }) })
  tokens.push({
    name: "DisConVal",
    value: FinancingHelper.bracketFormatter(
      REC.totalEquitySummary *
      REC.minorityShareholderDiscount?.ownershipInterestPercentage / 100 *
      REC.minorityShareholderDiscount?.discountForControlPercentageFloat / 100
    ) })
  tokens.push({
    name: "DisLackVal",
    value: FinancingHelper.bracketFormatter(
      REC.totalEquitySummary * REC.minorityShareholderDiscount?.ownershipInterestPercentage / 100 *
      (1 - REC.minorityShareholderDiscount?.discountForControlPercentageFloat / 100) *
      REC.minorityShareholderDiscount?.discountForLackOfMarketabilityFloat / 100
    )
  })
  //charts Screenshot Generation
  const forecastedRevenueChartBase64 = await Converter.fromHtmlToBase64Image(charts?.forecastedRevenueChartElement)
  const forecastedDOEChartBase64 = await Converter.fromHtmlToBase64Image(charts?.forecastedDOEChartElement)

  let images = {
    "logoBase64": generalInfo?.logo,
    "forecastedRevenueChartBase64": forecastedRevenueChartBase64,
    "forecastedDOEChartBase64": forecastedDOEChartBase64
  }
  
  const documentID = await FinancialHandler.createDashboardReport({ type: "Generate", tokens, images, reportType, reportFormat })

  if(reportFormat == "PDF") {
    await dispatch(setReportIsLoading("Downloading PDF Report.."));
    await FinancialHandler.createDashboardReport({ type: "Download", documentID });
  }
  else {
    await dispatch(setReportIsLoading("Downloading DOCX Report.."));
    await FinancialHandler.createDashboardReport({ type: "DOCX", documentID });
  }

  dispatch({ type: SET_DASHBOARD_REPORT_DATA, payload: { tokens, images, reportType, reportFormat } })
}
